import { useEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";

type FilterValue = string | string[] | number | undefined;

interface UrlFilterSyncOptions {
  /**
   * The form instance from react-hook-form
   */
  form: UseFormReturn<any>;

  /**
   * Map of form field names to URL parameter names
   * Example: { creatorType: "creatorType", riskTier: "riskTier" }
   */
  fieldMapping: Record<string, string>;

  /**
   * Navigate function from TanStack Router
   */
  navigate: (options: { search: (prev: any) => any; replace?: boolean }) => void;

  /**
   * Current URL search params
   */
  currentSearch: Record<string, any>;
}

/**
 * Custom hook to sync form filter values to URL parameters
 *
 * This enables:
 * - Real-time URL updates as filters change
 * - Persistent filter state on page reload
 *
 * @example
 * ```tsx
 * const { creatorType, riskTier } = route.useSearch();
 * const navigate = route.useNavigate();
 * const form = useForm();
 *
 * useUrlFilterSync({
 *   form,
 *   fieldMapping: { creatorType: "creatorType", riskTier: "riskTier" },
 *   navigate,
 *   currentSearch: { creatorType, riskTier }
 * });
 * ```
 */
export function useUrlFilterSync({ form, fieldMapping, navigate, currentSearch }: UrlFilterSyncOptions) {
  const isInitialMount = useRef(true);
  const fieldNames = Object.keys(fieldMapping);

  // Watch all mapped form fields
  const formValues = form.watch(fieldNames);

  useEffect(() => {
    // Skip on initial mount to avoid overwriting URL params
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Build new search params from form values
    const newSearch: Record<string, FilterValue> = {};
    let hasChanges = false;

    for (const [formField, urlParam] of Object.entries(fieldMapping)) {
      const formValue = form.getValues(formField);
      const urlValue = currentSearch[urlParam];

      // Normalize values for comparison
      const normalizedFormValue = normalizeValue(formValue);
      const normalizedUrlValue = normalizeValue(urlValue);

      // Check if value changed
      if (!areValuesEqual(normalizedFormValue, normalizedUrlValue)) {
        hasChanges = true;
      }

      // Only include non-empty values in URL
      if (normalizedFormValue !== undefined) {
        newSearch[urlParam] = normalizedFormValue;
      }
    }

    // Only update URL if there are actual changes
    if (hasChanges) {
      navigate({
        search: (prev) => {
          const updated = { ...prev };

          // Update or remove each mapped field
          for (const [_, urlParam] of Object.entries(fieldMapping)) {
            if (newSearch[urlParam] !== undefined) {
              // Set the new value
              updated[urlParam] = newSearch[urlParam];
            } else if (urlParam in updated) {
              // Explicitly delete undefined values to remove from URL
              delete updated[urlParam];
            }
          }

          return updated;
        },
        replace: true, // Use replace to avoid polluting browser history
      });
    }
  }, [formValues, form, fieldMapping, navigate, currentSearch]);
}

/**
 * Normalize filter values for consistent comparison
 */
function normalizeValue(value: any): FilterValue {
  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  // Handle empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return undefined;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    // Filter out empty values
    const filtered = value.filter((v) => v !== null && v !== undefined && v !== "");

    if (filtered.length === 0) return undefined;

    // Keep objects as-is (for filters that need {id, label} format)
    return filtered;
  }

  return value;
}

function deepEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => deepEquals(val, b[idx]));
  }

  const keysA = Object.keys(a as Record<string, unknown>);
  const keysB = Object.keys(b as Record<string, unknown>);
  if (keysA.length !== keysB.length) return false;

  return keysA.every(
    (key) =>
      Object.hasOwn(b, key) && deepEquals((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
  );
}

function areValuesEqual(a: FilterValue, b: FilterValue): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => {
      if (typeof val === "object" && val !== null && typeof b[idx] === "object" && b[idx] !== null) {
        return JSON.stringify(val) === JSON.stringify(b[idx]);
      }
      return val === b[idx];
    });
  }

  return a === b;
}
